"use client";

import { useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import { Phone, MessageCircle, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP, ScrollTrigger);
export default function Hero() {
  const container = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set([".hero-eyebrow", ".hero-line-1", ".hero-subhead", ".hero-ctas", ".hero-scroll"], {
        y: 20
      });
      const tl = gsap.timeline({
        defaults: {
          ease: "power3.out",
          duration: 0.72
        }
      });
      tl.to(".hero-eyebrow", {
        opacity: 1,
        y: 0
      }).to(".hero-line-1", {
        opacity: 1,
        y: 0
      }, "-=0.55").to(".hero-subhead", {
        opacity: 1,
        y: 0
      }, "-=0.5").to(".hero-ctas", {
        opacity: 1,
        y: 0
      }, "-=0.5").to(".hero-scroll", {
        opacity: 1,
        y: 0
      }, "-=0.5");
      gsap.to(".hero-bg", {
        yPercent: -8,
        ease: "none",
        scrollTrigger: {
          trigger: container.current,
          start: "top top",
          end: "bottom top",
          scrub: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set([".hero-eyebrow", ".hero-line-1", ".hero-subhead", ".hero-ctas", ".hero-scroll"], {
        opacity: 1,
        y: 0
      });
    });
  }, {
    scope: container
  });
  return <section data-section-id="32766" id="excelencia-en-mantenimiento-y-reparaciones" data-nav="dark" ref={container} className="dark bg-[hsl(217,62%,9%)] text-foreground relative overflow-hidden min-h-[88vh] md:min-h-[92vh] w-full">
      <div className="absolute inset-0 z-0 hero-bg">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1753482/img/mediterranean_villa_property.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1753482/img/mediterranean_villa_property.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1753482/img/mediterranean_villa_property.webp-wvc-sizes" alt="Vivienda mediterránea moderna con fachada blanca y vegetación cuidada" className="h-full w-full object-cover opacity-60" />
      </div>

      <div className="absolute inset-0 z-10 pointer-events-none" style={{
      background: "radial-gradient(ellipse at 20% 50%, hsl(217 62% 9% / 0.82) 0%, hsl(217 62% 9% / 0.6) 50%, hsl(217 62% 9% / 0.75) 100%)"
    }} />
      <div className="absolute inset-x-0 bottom-0 h-1/2 z-10 pointer-events-none" style={{
      background: "linear-gradient(to bottom, hsl(217 62% 9% / 0) 0%, hsl(217 62% 9% / 0.6) 60%, hsl(217 62% 9% / 0.95) 100%)"
    }} />

      <div className="relative z-20 min-h-[88vh] md:min-h-[92vh] mx-auto max-w-[88rem] px-6 md:px-10 lg:px-16 flex flex-col">
        <div className="grid grid-cols-12 gap-6 md:gap-8 flex-1 items-end pb-16 md:pb-24 pt-40 md:pt-0">
          <div className="col-span-12 md:col-span-11 lg:col-span-10" style={{
          marginTop: "auto"
        }}>
            <div className="hero-eyebrow flex items-center gap-3 mb-5" style={{
            opacity: 0
          }}>
              <span className="inline-flex items-center rounded-lg bg-[hsl(18,78%,48%)] px-3.5 py-1.5 text-xs font-bold uppercase tracking-wider text-white shadow-xs">
                Urgencias 24 horas
              </span>
            </div>

            <h1 className="font-mono uppercase text-foreground" style={{
            fontSize: "clamp(2.25rem, 5.2vw, 4.75rem)",
            fontWeight: 700,
            letterSpacing: "-0.01em",
            lineHeight: 1.05
          }}>
              <span className="block overflow-hidden">
                <span className="hero-line-1 block" style={{
                opacity: 0
              }}>
                  Reparación y mantenimiento profesional para tu propiedad
                </span>
              </span>
            </h1>

            <p className="hero-subhead mt-7 max-w-3xl font-default text-balance" style={{
            opacity: 0,
            color: "hsl(215 20% 75% / 0.9)",
            fontSize: "clamp(1.0625rem, 1.35vw, 1.25rem)",
            lineHeight: 1.55,
            fontWeight: 400
          }}>
              Servicios de fontanería, detección de fugas, climatización, piscinas, reformas y mantenimiento para viviendas, comunidades y propiedades vacacionales.
            </p>

            <div className="hero-ctas mt-10 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3.5 sm:gap-4" style={{
            opacity: 0
          }}>
              {/* Botón 1 - Llamar ahora */}
              <Button asChild className="rounded-lg h-auto px-6 py-3.5 text-sm font-semibold uppercase bg-[hsl(18,78%,48%)] text-white hover:bg-[hsl(18,78%,42%)] shadow-xs w-full sm:w-auto" style={{
              letterSpacing: "0.02em"
            }}>
                <a href="tel:+34657068444">
                  <Phone className="size-4 shrink-0" aria-hidden="true" />
                  <span>Llamar ahora</span>
                </a>
              </Button>

              {/* Botón 2 - WhatsApp */}
              <Button asChild className="rounded-lg h-auto px-6 py-3.5 text-sm font-semibold uppercase bg-[#25D366] text-white hover:bg-[#20ba59] shadow-xs w-full sm:w-auto" style={{
              letterSpacing: "0.02em"
            }}>
                <Link to="https://wa.me/34657068444" newTab>
                  <MessageCircle className="size-4 shrink-0" aria-hidden="true" />
                  <span>WhatsApp</span>
                </Link>
              </Button>

              {/* Botón 3 - Solicitar presupuesto */}
              <Button asChild variant="outline" className="rounded-lg h-auto px-6 py-3.5 text-sm font-semibold uppercase bg-transparent border text-foreground hover:bg-foreground/10 hover:text-foreground w-full sm:w-auto" style={{
              letterSpacing: "0.02em",
              borderColor: "hsl(216 40% 26% / 0.8)"
            }}>
                <Link to="/ContactPage">
                  <FileText className="size-4 shrink-0" aria-hidden="true" />
                  <span>Solicitar presupuesto</span>
                </Link>
              </Button>
            </div>
          </div>
        </div>

        <div className="hero-scroll absolute bottom-8 right-6 md:right-10 lg:right-16 hidden md:flex flex-col items-center gap-3" style={{
        opacity: 0
      }}>
          <span className="block w-px" style={{
          height: "40px",
          backgroundColor: "hsl(18 78% 48%)"
        }} aria-hidden="true" />
          <span className="font-default uppercase font-semibold" style={{
          color: "hsl(215 20% 75% / 0.85)",
          fontSize: "0.6875rem",
          letterSpacing: "0.24em",
          writingMode: "vertical-rl",
          transform: "rotate(180deg)"
        }}>
            Desplazar
          </span>
        </div>
      </div>
    </section>;
}